/**
 * Sistema de Control de Acceso Basado en Roles (RBAC)
 * AllTuc Marketplace - Sistema de permisos unificado
 */

// ============================================================
// ROLES DEL SISTEMA
// ============================================================

export type AppRole = 
  | 'superadmin'    // Control total del sistema
  | 'admin'         // Administrador general
  | 'moderator'     // Moderador de contenido
  | 'support'       // Soporte al cliente
  | 'partner_owner' // Dueño de tienda
  | 'partner_manager' // Gerente de tienda
  | 'partner_staff' // Empleado de tienda
  | 'customer';     // Cliente/usuario regular

// ============================================================
// RECURSOS DEL SISTEMA
// ============================================================

export type Resource = 
  // Productos
  | 'products'
  | 'product_reviews'
  | 'product_questions'
  | 'categories'
  
  // Órdenes
  | 'orders'
  | 'order_assignments'
  | 'shipments'
  
  // Partners y Tiendas
  | 'partners'
  | 'shops'
  | 'shop_staff'
  | 'shop_finances'
  | 'shop_analytics'
  
  // Usuarios
  | 'users'
  | 'user_roles'
  | 'user_permissions'
  
  // Contenido
  | 'banners'
  | 'blog_posts'
  | 'stories'
  | 'marketing'
  
  // Sistema
  | 'settings'
  | 'config'
  | 'seo'
  | 'emails'
  | 'notifications'
  | 'support_tickets'
  | 'transactions'
  | 'commissions'
  | 'withdrawals'
  | 'club'
  | 'reports';

// ============================================================
// ACCIONES
// ============================================================

export type Action = 
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'manage'      // CRUD completo
  | 'approve'
  | 'reject'
  | 'suspend'
  | 'assign'
  | 'export'
  | 'import'
  | 'view_own'    // Solo ver lo propio
  | 'manage_own'; // Solo gestionar lo propio

// ============================================================
// PERMISO INDIVIDUAL
// ============================================================

export interface Permission {
  resource: Resource;
  action: Action;
  conditions?: {
    ownOnly?: boolean;      // Solo recursos propios
    shopOnly?: boolean;     // Solo de la tienda asignada
    statusCheck?: string[]; // Solo ciertos estados
  };
}

// ============================================================
// CONFIGURACIÓN DE ROLES
// ============================================================

export interface RoleConfig {
  role: AppRole;
  displayName: string;
  description: string;
  level: number; // Jerarquía: mayor = más permisos
  permissions: Permission[];
  inheritsFrom?: AppRole; // Hereda permisos de otro rol
}

// ============================================================
// ROLES PREDEFINIDOS DEL SISTEMA
// ============================================================

export const SYSTEM_ROLES: RoleConfig[] = [
  // SUPERADMIN - Control total
  {
    role: 'superadmin',
    displayName: 'Super Administrador',
    description: 'Control total del sistema. Puede hacer todo.',
    level: 100,
    permissions: [
      { resource: 'products', action: 'manage' },
      { resource: 'orders', action: 'manage' },
      { resource: 'partners', action: 'manage' },
      { resource: 'shops', action: 'manage' },
      { resource: 'users', action: 'manage' },
      { resource: 'user_roles', action: 'manage' },
      { resource: 'user_permissions', action: 'manage' },
      { resource: 'categories', action: 'manage' },
      { resource: 'banners', action: 'manage' },
      { resource: 'blog_posts', action: 'manage' },
      { resource: 'stories', action: 'manage' },
      { resource: 'marketing', action: 'manage' },
      { resource: 'settings', action: 'manage' },
      { resource: 'config', action: 'manage' },
      { resource: 'seo', action: 'manage' },
      { resource: 'emails', action: 'manage' },
      { resource: 'notifications', action: 'manage' },
      { resource: 'support_tickets', action: 'manage' },
      { resource: 'transactions', action: 'manage' },
      { resource: 'commissions', action: 'manage' },
      { resource: 'withdrawals', action: 'manage' },
      { resource: 'club', action: 'manage' },
      { resource: 'reports', action: 'manage' },
      { resource: 'product_reviews', action: 'manage' },
      { resource: 'product_questions', action: 'manage' },
    ]
  },

  // ADMIN - Administrador General
  {
    role: 'admin',
    displayName: 'Administrador',
    description: 'Gestión general del marketplace.',
    level: 80,
    permissions: [
      { resource: 'products', action: 'manage' },
      { resource: 'orders', action: 'manage' },
      { resource: 'partners', action: 'manage' },
      { resource: 'shops', action: 'read' },
      { resource: 'users', action: 'manage' },
      { resource: 'categories', action: 'manage' },
      { resource: 'banners', action: 'manage' },
      { resource: 'blog_posts', action: 'manage' },
      { resource: 'stories', action: 'manage' },
      { resource: 'marketing', action: 'manage' },
      { resource: 'config', action: 'read' },
      { resource: 'seo', action: 'manage' },
      { resource: 'emails', action: 'manage' },
      { resource: 'support_tickets', action: 'manage' },
      { resource: 'transactions', action: 'read' },
      { resource: 'commissions', action: 'read' },
      { resource: 'withdrawals', action: 'manage' },
      { resource: 'club', action: 'manage' },
      { resource: 'reports', action: 'read' },
      { resource: 'product_reviews', action: 'manage' },
      { resource: 'product_questions', action: 'manage' },
    ]
  },

  // MODERATOR - Moderador de Contenido
  {
    role: 'moderator',
    displayName: 'Moderador',
    description: 'Moderación de contenido y reviews.',
    level: 60,
    permissions: [
      { resource: 'products', action: 'read' },
      { resource: 'products', action: 'update', conditions: { statusCheck: ['pending_review'] } },
      { resource: 'orders', action: 'read' },
      { resource: 'partners', action: 'read' },
      { resource: 'shops', action: 'read' },
      { resource: 'users', action: 'read' },
      { resource: 'blog_posts', action: 'manage' },
      { resource: 'stories', action: 'manage' },
      { resource: 'product_reviews', action: 'manage' },
      { resource: 'product_questions', action: 'manage' },
      { resource: 'support_tickets', action: 'manage' },
    ]
  },

  // SUPPORT - Soporte al Cliente
  {
    role: 'support',
    displayName: 'Soporte',
    description: 'Atención al cliente y gestión de tickets.',
    level: 50,
    permissions: [
      { resource: 'products', action: 'read' },
      { resource: 'orders', action: 'read' },
      { resource: 'orders', action: 'update', conditions: { statusCheck: ['pending', 'approved'] } },
      { resource: 'partners', action: 'read' },
      { resource: 'shops', action: 'read' },
      { resource: 'users', action: 'read' },
      { resource: 'support_tickets', action: 'manage' },
    ]
  },

  // PARTNER_OWNER - Dueño de Tienda
  {
    role: 'partner_owner',
    displayName: 'Dueño de Tienda',
    description: 'Control total de su tienda y productos.',
    level: 40,
    permissions: [
      { resource: 'products', action: 'manage', conditions: { ownOnly: true } },
      { resource: 'orders', action: 'manage', conditions: { shopOnly: true } },
      { resource: 'shop_staff', action: 'manage', conditions: { ownOnly: true } },
      { resource: 'shop_finances', action: 'read', conditions: { ownOnly: true } },
      { resource: 'shop_analytics', action: 'read', conditions: { ownOnly: true } },
      { resource: 'withdrawals', action: 'create', conditions: { ownOnly: true } },
      { resource: 'product_reviews', action: 'read', conditions: { ownOnly: true } },
      { resource: 'product_questions', action: 'manage', conditions: { ownOnly: true } },
    ]
  },

  // PARTNER_MANAGER - Gerente de Tienda
  {
    role: 'partner_manager',
    displayName: 'Gerente de Tienda',
    description: 'Gestión de productos y órdenes de la tienda.',
    level: 35,
    inheritsFrom: 'partner_staff',
    permissions: [
      { resource: 'products', action: 'manage', conditions: { shopOnly: true } },
      { resource: 'orders', action: 'manage', conditions: { shopOnly: true } },
      { resource: 'shop_staff', action: 'read', conditions: { shopOnly: true } },
      { resource: 'shop_analytics', action: 'read', conditions: { shopOnly: true } },
      { resource: 'product_reviews', action: 'read', conditions: { shopOnly: true } },
      { resource: 'product_questions', action: 'manage', conditions: { shopOnly: true } },
    ]
  },

  // PARTNER_STAFF - Empleado de Tienda
  {
    role: 'partner_staff',
    displayName: 'Empleado',
    description: 'Acceso básico a productos y órdenes.',
    level: 30,
    permissions: [
      { resource: 'products', action: 'read', conditions: { shopOnly: true } },
      { resource: 'products', action: 'create', conditions: { shopOnly: true } },
      { resource: 'products', action: 'update', conditions: { shopOnly: true } },
      { resource: 'orders', action: 'read', conditions: { shopOnly: true } },
      { resource: 'orders', action: 'update', conditions: { shopOnly: true, statusCheck: ['approved', 'shipped'] } },
      { resource: 'product_questions', action: 'read', conditions: { shopOnly: true } },
    ]
  },

  // CUSTOMER - Cliente
  {
    role: 'customer',
    displayName: 'Cliente',
    description: 'Usuario regular del marketplace.',
    level: 10,
    permissions: [
      { resource: 'products', action: 'read' },
      { resource: 'orders', action: 'read', conditions: { ownOnly: true } },
      { resource: 'orders', action: 'create' },
    ]
  },
];

// ============================================================
// FUNCIONES AUXILIARES
// ============================================================

/**
 * Verifica si un rol tiene un permiso específico
 */
export function hasPermission(
  role: AppRole,
  resource: Resource,
  action: Action,
  customPermissions?: Permission[]
): boolean {
  // Superadmin siempre tiene todos los permisos
  if (role === 'superadmin') return true;

  // Obtener configuración del rol
  const roleConfig = SYSTEM_ROLES.find(r => r.role === role);
  if (!roleConfig) return false;

  // Combinar permisos heredados y propios
  let permissions = [...roleConfig.permissions];
  
  if (roleConfig.inheritsFrom) {
    const parentRole = SYSTEM_ROLES.find(r => r.role === roleConfig.inheritsFrom);
    if (parentRole) {
      permissions = [...parentRole.permissions, ...permissions];
    }
  }

  // Agregar permisos personalizados si existen
  if (customPermissions) {
    permissions = [...permissions, ...customPermissions];
  }

  // Buscar permiso exacto o permiso 'manage'
  return permissions.some(p => 
    p.resource === resource && 
    (p.action === action || p.action === 'manage')
  );
}

/**
 * Verifica si un rol puede gestionar recursos propios
 */
export function canManageOwn(role: AppRole, resource: Resource): boolean {
  return hasPermission(role, resource, 'manage_own') || 
         hasPermission(role, resource, 'manage');
}

/**
 * Obtiene todos los permisos de un rol
 */
export function getRolePermissions(role: AppRole): Permission[] {
const roleConfig = SYSTEM_ROLES.find(r => r.role === role);
  if (!roleConfig) return [];

  const permissions = [...roleConfig.permissions];

  if (roleConfig.inheritsFrom) {
    const parentRole = SYSTEM_ROLES.find(r => r.role === roleConfig.inheritsFrom);
    if (parentRole) {
      // Filtrar duplicados
      const parentResources = new Set(permissions.map(p => `${p.resource}:${p.action}`));
      parentRole.permissions.forEach(p => {
        if (!parentResources.has(`${p.resource}:${p.action}`)) {
          permissions.push(p);
        }
      });
    }
  }

  return permissions;
}

/**
 * Obtiene el nivel jerárquico de un rol
 */
export function getRoleLevel(role: AppRole): number {
  const roleConfig = SYSTEM_ROLES.find(r => r.role === role);
  return roleConfig?.level || 0;
}

/**
 * Verifica si un rol es superior a otro
 */
export function isRoleHigher(role: AppRole, compareTo: AppRole): boolean {
  return getRoleLevel(role) > getRoleLevel(compareTo);
}

/**
 * Lista todos los roles disponibles para asignación
 */
export function getAssignableRoles(byRole: AppRole): RoleConfig[] {
  const byLevel = getRoleLevel(byRole);
  return SYSTEM_ROLES.filter(r => r.level < byLevel);
}

// ============================================================
// CONSTANTES
// ============================================================

// Roles que tienen acceso al admin panel
export const ADMIN_ROLES: AppRole[] = ['superadmin', 'admin', 'moderator', 'support'];

// Roles de partner
export const PARTNER_ROLES: AppRole[] = ['partner_owner', 'partner_manager', 'partner_staff'];

// Roles que pueden vender
export const SELLER_ROLES: AppRole[] = ['partner_owner', 'partner_manager', 'partner_staff'];
